import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, updateDoc, setDoc, deleteDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './components/Sidebar';
import { useHeader } from '../contexts/HeaderContext';
import PitchBuilderToolbar from './components/PitchBuilderToolbar';
import {
  Building2,
  LineChart,
  Users,
  FileText,
  DollarSign,
  FileCheck,
  Video
} from 'lucide-react';
import {
  CompanyInfo,
  KeyMetrics,
  TeamSection,
  PitchDeck,
  Financials,
  DueDiligence,
  ProductDemo
} from './sections';

// Mock data for demo company
const demoCompanyData = {
  name: 'Stripe',
  tagline: 'Payments infrastructure for the internet',
  logo: 'ST',
  logoUrl: 'https://stripe.com/logo.png',
  website: 'https://stripe.com',
  founded: '2010-09-01',
  location: 'San Francisco, CA',
  stage: 'growth',
  metrics: {
    seeking: '$10M',
    growth: '300%',
    users: '2M+',
    arr: '$7B+'
  },
  teamMembers: [
    {
      firstName: 'Patrick',
      lastName: 'Collison',
      role: 'CEO & Co-founder',
      bio: 'Irish entrepreneur, programmer, and co-founder of Stripe. Forbes 30 Under 30.',
      initials: 'PC',
      email: 'patrick@stripe.com'
    },
    {
      firstName: 'John',
      lastName: 'Collison',
      role: 'President & Co-founder',
      bio: 'Irish entrepreneur, co-founder of Stripe, youngest self-made billionaire (Forbes).',
      initials: 'JC',
      email: 'john@stripe.com'
    }
  ],
  pitchDeck: {
    uploaded: true,
    fileName: 'Stripe_Seed_Pitch_Deck.pdf',
    slides: 15,
    lastUpdated: '2010-08-31T00:00:00.000Z'
  },
  financials: {
    year1: '$1.5M',
    year2: '$5M', 
    year3: '$20M',
    year4: '$100M',
    year5: '$500M',
    burnRate: '$200K',
    cac: '$50',
    ltv: '$2,000',
    churn: '0.5%',
    breakeven: 'Q4 2012'
  },
  documents: [
    { name: 'Articles of Incorporation', uploaded: true, required: true, fileName: 'articles.pdf', lastUpdated: '2010-08-31T00:00:00.000Z' },
    { name: 'Cap Table & Equity Structure', uploaded: true, required: true, fileName: 'cap_table.pdf', lastUpdated: '2010-08-31T00:00:00.000Z' },
    { name: 'IP Portfolio & Patents', uploaded: true, required: true, fileName: 'ip_portfolio.pdf', lastUpdated: '2010-08-31T00:00:00.000Z' },
    { name: 'SOC 2 Compliance Report', uploaded: false, required: false },
    { name: 'Insurance Documentation', uploaded: false, required: false },
    { name: 'Previous Funding History', uploaded: false, required: false },
    { name: 'Board Meeting Minutes', uploaded: false, required: false },
    { name: 'Customer References', uploaded: false, required: false }
  ],
  productDemo: {
    videoUrl: 'https://www.youtube.com/watch?v=QUs3Apy8IMA&ab_channel=StartupGrind',
    videoName: 'Stripe_Platform_Overview.mp4',
    lastUpdated: new Date().toISOString(),
    demoLink: 'https://dashboard.stripe.com/test',
    features: [
      'Instant payment processing',
      'Global currency support',
      'Developer-friendly APIs',
      'Fraud detection',
      'Scalable infrastructure'
    ]
  }
};

const sections = [
  { id: 'company-info', title: 'Company Info', icon: Building2 },
  { id: 'metrics', title: 'Key Metrics', icon: LineChart },
  { id: 'team', title: 'Team', icon: Users },
  { id: 'pitch-deck', title: 'Pitch Deck', icon: FileText },
  { id: 'financials', title: 'Financials', icon: DollarSign },
  { id: 'due-diligence', title: 'Due Diligence', icon: FileCheck },
  { id: 'product-demo', title: 'Product Demo', icon: Video }
];

const PitchBuilder = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeSection, setActiveSection] = useState('company-info');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    logo: '',
    logoUrl: '',
    website: '',
    founded: '',
    location: '',
    stage: '',
    metrics: {
      seeking: '',
      growth: '',
      users: '',
      arr: ''
    },
    teamMembers: [
      {
        firstName: '',
        lastName: '',
        role: '',
        bio: '',
        initials: '',
        email: ''
      }
    ],
    pitchDeck: {
      uploaded: false,
      fileName: '',
      slideCount: 0,
      lastUpdated: null
    },
    financials: {
      year1: '',
      year2: '',
      year3: '',
      year4: '',
      year5: '',
      burnRate: '',
      cac: '',
      ltv: '',
      churnRate: '',
      breakevenPoint: ''
    },
    documents: [
      { name: 'Business Plan', required: true, uploaded: false, lastUpdated: null },
      { name: 'Financial Statements', required: true, uploaded: false, lastUpdated: null },
      { name: 'Market Analysis', required: true, uploaded: false, lastUpdated: null },
      { name: 'Legal Documents', required: false, uploaded: false, lastUpdated: null },
      { name: 'Patents/IP', required: false, uploaded: false, lastUpdated: null }
    ],
    productDemo: {
      videoUrl: '',
      features: '',
      demoLink: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [handle, setHandle] = useState('');
  const [handleStatus, setHandleStatus] = useState('idle'); // idle | checking | available | taken | saved | error
  const [isHandleSaving, setIsHandleSaving] = useState(false);
  const [lastSavedData, setLastSavedData] = useState(formData);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const { setHeaderOptions } = useHeader();

  // Debounced auto-save function
  const debouncedSave = useCallback((data) => {
    if (companyId === 'demo') return;
    
    // Clear any existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      setIsAutoSaving(true);
      try {
        // Only save if data has actually changed
        if (JSON.stringify(data) !== JSON.stringify(lastSavedData)) {
          await updateDoc(doc(db, 'companies', companyId), data);
          setLastSavedData(data);
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 2000);
        }
      } catch (error) {
        console.error('Error auto-saving company data:', error);
      } finally {
        setIsAutoSaving(false);
      }
    }, 1000); // 1 second delay

    setAutoSaveTimeout(timeout);
  }, [companyId, lastSavedData]);

  // Effect to handle auto-saving when formData changes
  useEffect(() => {
    if (companyId === 'demo') return;
    
    // Only save if data has actually changed
    if (JSON.stringify(formData) !== JSON.stringify(lastSavedData)) {
      debouncedSave(formData);
    }

    // Cleanup timeout on unmount
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [formData, lastSavedData, debouncedSave, companyId]);

  // Update lastSavedData when initial data is loaded
  useEffect(() => {
    const fetchCompanyData = async () => {
      setLoading(true);
      try {
        if (companyId === 'demo') {
          setFormData(demoCompanyData);
          setLastSavedData(demoCompanyData);
        } else {
          const companyDoc = await getDoc(doc(db, 'companies', companyId));
          if (companyDoc.exists()) {
            // Ensure logoUrl is present, else default to ''
            const data = companyDoc.data();
            const formattedData = {
              ...data,
              logoUrl: data.logoUrl || '',
            };
            setFormData(formattedData);
            setLastSavedData(formattedData);
          }
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [companyId]);

  // Real-time uniqueness check (use lowercase to match how we persist)
  useEffect(() => {
    if (!handle || handle.length < 4) {
      setHandleStatus('idle');
      return;
    }
    let active = true;
    setHandleStatus('checking');
    const check = async () => {
      const normalizedHandle = handle.toLowerCase().trim();
      const ref = doc(db, 'handles', normalizedHandle);
      const snap = await getDoc(ref);
      if (!active) return;
      if (snap.exists()) {
        const data = snap.data();
        if (data.companyId === companyId && data.uid === currentUser?.uid) {
          setHandleStatus('saved');
        } else {
          setHandleStatus('taken');
        }
      } else {
        setHandleStatus('available');
      }
    };
    check();
    return () => { active = false; };
  }, [handle, currentUser, companyId]);

  const handleHandleChange = (e) => {
    setHandle(e.target.value);
    setHandleStatus('idle');
  };

  const onSaveHandle = async () => {
    if (handleStatus !== 'available' || handle.length < 4 || !currentUser || !companyId) return;
    setIsHandleSaving(true);
    const lowercaseHandle = handle.toLowerCase().trim();
    try {
      // Remove any existing handle(s) for this company so only one handle per company persists
      const handlesRef = collection(db, 'handles');
      const qh = query(handlesRef, where('companyId', '==', companyId));
      const existingSnap = await getDocs(qh);
      const batch = [];
      for (const d of existingSnap.docs) {
        if (d.id !== lowercaseHandle) {
          batch.push(deleteDoc(d.ref));
        }
      }
      await Promise.all(batch);

      // Persist the new handle to Firestore
      await setDoc(doc(db, 'handles', lowercaseHandle), {
        uid: currentUser.uid,
        companyId,
      });

      // Update company document with handle for single source of truth and reliable preload
      await updateDoc(doc(db, 'companies', companyId), { handle: lowercaseHandle });

      setHandle(lowercaseHandle);
      setHandleStatus('saved');
    } catch (e) {
      console.error('Error saving handle to Firebase:', e);
      setHandleStatus('error');
    } finally {
      setIsHandleSaving(false);
    }
  };

  useEffect(() => {
    // Only run if companyId and currentUser are available
    if (!companyId || !currentUser) return;

    const preloadHandle = async () => {
      try {
        // Prefer handle stored on company document (single source of truth after save)
        const companySnap = await getDoc(doc(db, 'companies', companyId));
        const companyHandle = companySnap.exists() ? companySnap.data().handle : null;
        if (companyHandle) {
          setHandle(companyHandle);
          setHandleStatus('saved');
          return;
        }
        // Fallback: query handles collection by companyId
        const handlesRef = collection(db, 'handles');
        const qh = query(handlesRef, where('companyId', '==', companyId));
        const snap = await getDocs(qh);
        if (!snap.empty) {
          const docSnap = snap.docs[0];
          setHandle(docSnap.id);
          setHandleStatus('saved');
        }
      } catch (err) {
        console.error('Error preloading handle:', err);
      }
    };

    preloadHandle();
  }, [companyId, currentUser]);

  useEffect(() => {
    if (companyId === 'demo') {
      setHandle('demo-preview');
      setHandleStatus('saved');
    }
  }, [companyId]);

  const handleInputChange = (section, field, value) => {
    if (companyId === 'demo') {
      // Don't allow changes to demo data
      return;
    }
    
    setFormData(prev => {
      let newData;
      
      // Handle team member updates
      if (section === 'teamMembers' && typeof field === 'number') {
        const updatedTeamMembers = [...prev.teamMembers];
        updatedTeamMembers[field] = {
          ...updatedTeamMembers[field],
          ...value
        };
        newData = {
          ...prev,
          teamMembers: updatedTeamMembers
        };
      }
      
      // Handle nested objects (like metrics, financials, etc.)
      else if (typeof prev[section] === 'object' && !Array.isArray(prev[section])) {
        newData = {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      }
      
      // Handle arrays (like documents)
      else if (Array.isArray(prev[section])) {
        newData = {
          ...prev,
          [section]: value
        };
      }
      
      // Handle simple values
      else {
        newData = {
          ...prev,
          [section]: value
        };
      }

      return newData;
    });
  };

  // Manual save function (kept for explicit saves)
  const handleSave = async () => {
    if (companyId === 'demo') return;
    
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'companies', companyId), formData);
      setLastSavedData(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving company data:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    if (companyId === 'demo') {
      navigate('/demo-preview');
    } else {
      navigate(`/preview/${companyId}`);
    }
  };

  const renderSection = () => {
    const props = {
      formData,
      onInputChange: handleInputChange,
      isDemo: companyId === 'demo'
    };

    switch (activeSection) {
      case 'company-info':
        return <CompanyInfo {...props} />;
      case 'metrics':
        return <KeyMetrics {...props} />;
      case 'team':
        return <TeamSection {...props} />;
      case 'pitch-deck':
        return <PitchDeck {...props} companyId={companyId} />;
      case 'financials':
        return <Financials {...props} />;
      case 'due-diligence':
        return <DueDiligence {...props} />;
      case 'product-demo':
        return <ProductDemo {...props} />;
      default:
        return null;
    }
  };

  // Format section name for header
  const formatSectionName = (section) => {
    return section.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Set header options for pitch builder
  useEffect(() => {
    setHeaderOptions({
      showHamburger: true,
      onHamburgerClick: () => setIsMobileMenuOpen(!isMobileMenuOpen),
      sectionTitle: formatSectionName(activeSection),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, setIsMobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Pitch builder toolbar below the header */}
      <PitchBuilderToolbar
        handle={handle}
        onHandleChange={handleHandleChange}
        handleStatus={handleStatus}
        onSaveHandle={onSaveHandle}
        isHandleSaving={isHandleSaving}
        handleDisabled={companyId === 'demo'}
        saveSuccess={saveSuccess}
        companyId={companyId}
        isSaving={isSaving}
        isAutoSaving={isAutoSaving}
        onSave={handleSave}
        activeSection={activeSection}
        currentUser={currentUser}
      />
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center min-h-[300px] text-lg text-gray-500">Loading...</div>
        ) : (
          <div className="flex gap-8">
            <Sidebar
              sections={sections}
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              isMobileMenuOpen={isMobileMenuOpen}
              onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
              formData={formData}
            />
            
            <main className="flex-1">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                {renderSection()}
              </div>
            </main>
          </div>
        )}
      </div>
    </div>
  );
};

export default PitchBuilder; 